import { supabase } from '@/lib/supabase'
import { ApiError, ApiResponse } from './types'

/**
 * @file Implements a centralized API client for interacting with Supabase Edge Functions.
 * @summary This client standardizes function invocation, error handling, and response parsing.
 * It uses direct fetch calls to preserve error messages from Edge Functions.
 */

/**
 * The core API client for invoking Supabase Edge Functions.
 * It provides a type-safe method to make API calls, handling the response
 * and error shapes consistently.
 */
class ApiClient {
  private getSupabaseUrl(): string {
    const url = import.meta.env.VITE_SUPABASE_URL
    if (!url) {
      throw new Error('Missing VITE_SUPABASE_URL environment variable')
    }
    return url
  }

  /**
   * Invokes a Supabase Edge Function with the given options.
   * Uses direct fetch to preserve error messages from Edge Functions.
   *
   * @template T - The expected type of the data in a successful response.
   * @param {string} functionName - The name of the Edge Function to invoke.
   * @param {object} [options] - The options to pass to the function, including body, headers, and method.
   * @param {string} [options.method] - The HTTP method to use (e.g., 'POST', 'GET').
   * @param {Record<string, unknown> | FormData} [options.body] - The payload to send to the function.
   * @returns {Promise<T>} - A promise that resolves with the data if the call is successful.
   * @throws {ApiError} - Throws an ApiError if the function returns an error or the call fails.
   */
  public async invoke<T>(
    functionName: string,
    options?: {
      method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
      body?: Record<string, unknown> | FormData
    }
  ): Promise<T> {
    try {
      // Get current session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new ApiError('No authentication session found')
      }

      // Construct Edge Function URL
      const supabaseUrl = this.getSupabaseUrl()
      const url = `${supabaseUrl}/functions/v1/${functionName}`

      // Determine if we're sending FormData or JSON
      const isFormData = options?.body instanceof FormData

      // Prepare headers - don't set Content-Type for FormData (browser will set it with boundary)
      const headers: Record<string, string> = {
        Authorization: `Bearer ${session.access_token}`,
        apiKey: session.access_token,
      }

      if (!isFormData) {
        headers['Content-Type'] = 'application/json'
      }

      // Prepare body
      let body: string | FormData | undefined
      if (options?.body) {
        body = isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body)
      }

      // Make direct fetch call to preserve error messages
      const response = await fetch(url, {
        method: options?.method || 'POST',
        headers,
        body,
      })

      if (response.ok && response.status === 204) {
        // Handle 204 No Content response
        return {} as T // Return empty object for 204 responses
      }

      // Parse response body regardless of HTTP status
      let responseData: ApiResponse<T>
      try {
        responseData = await response.json()
      } catch {
        throw new ApiError(
          `Failed to parse response: ${response.status} ${response.statusText}`,
          { status: response.status, statusText: response.statusText }
        )
      }

      // Handle structured error responses from Edge Functions
      if (responseData?.error) {
        console.error(responseData.error)
        throw new ApiError(responseData.error.message)
      }

      // Return successful data
      if (responseData?.data !== undefined) {
        return responseData.data
      }

      // Fallback for unexpected response structure
      throw new ApiError('Invalid API response structure.', {
        response: responseData,
        status: response.status,
      })
    } catch (error) {
      // Re-throw ApiError instances as-is
      if (error instanceof ApiError) {
        throw error
      }

      // Handle network errors and other fetch failures
      throw new ApiError(
        `Function invocation failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        error
      )
    }
  }
}

/**
 * Singleton instance of the ApiClient.
 * Use this instance for all Edge Function invocations.
 */
export const apiClient = new ApiClient()
